import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";

interface Call {
  id: string;
  subject: string;
  call_description: string;
  lead: string;
  status_2: string;
  call_date: string;
  call_time: string;
  lead_id: string;
}

export default function TodaysCalls() {
  const [calls, setCalls] = useState<Call[]>([]);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCalls = async () => {
      const token = sessionStorage.getItem("access_token");
      if (!token) {
        setError("Authorization required. Please log in.");
        navigate("/signin");
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/today/call`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();
        if (response.ok && data.status === 200) {
          setCalls(data.data);
        } else {
          setError("Failed to load calls.");
        }
      } catch (err) {
        console.error(err);
        setError("An error occurred while fetching calls.");
      }
    };

    fetchCalls();
  }, [navigate]);

  // Function to determine the color class for status_2
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "planned":
        return "text-blue-600 dark:text-blue-400";
      case "not held":
        return "text-red-600 dark:text-red-400";
      case "held":
        return "text-green-600 dark:text-green-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  // Function to handle row click
  const handleRowClick = (leadId: string) => {
    navigate(`/call-list/${leadId}`);
  };

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white px-5 pb-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Today's Calls
          </h3>
        </div>
        <div className="relative inline-block">
          <button
            onClick={() => navigate("/lead-list")}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 px-3 py-1 rounded-md text-sm font-medium"
          >
            View More
          </button>
        </div>
      </div>

      <div className="max-w-full overflow-visible">
        <Table className="w-full">
          <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
            <TableRow>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Subject
              </TableCell>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Lead
              </TableCell>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Status
              </TableCell>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Date & Time
              </TableCell>
            </TableRow>
          </TableHeader>

          <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
            {Array.isArray(calls) && calls.length > 0 ? (
              calls.map((call) => (
                <TableRow key={call.id}>
                  <TableCell>
                    <div
                      onClick={() => handleRowClick(call.lead_id)}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 py-3 text-black dark:text-white text-[12px]"
                    >
                      {call.subject}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div
                      onClick={() => handleRowClick(call.lead_id)}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 py-3 text-black dark:text-white text-[12px]"
                    >
                      {call.lead}
                    </div>
                  </TableCell>

                  {/* <TableCell>
                    <div
                      onClick={() => handleRowClick(call.lead_id)}
                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 py-3 ${getStatusColor(call.status_2)} text-[12px]`}
                    >
                      {call.status_2}
                    </div>
                  </TableCell> */}

                  <TableCell>
                    <div
                      onClick={() => handleRowClick(call.lead_id)}
                      className={`relative group ${getStatusColor(
                        call.status_2
                      )} cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 py-3 text-[12px]`}
                    >
                      {call.status_2}
                      {/* Tooltip */}
                      <div className="absolute hidden group-hover:flex flex-col items-center z-[999999] left-1/2 -translate-x-1/2 bottom-full w-80 p-2 bg-black text-white text-xs rounded-lg shadow-lg whitespace-pre-wrap text-justify">
                        <p className="line-clamp-4 overflow-hidden text-ellipsis">
                          {call.call_description}
                        </p>
                        {/* Tooltip arrow */}
                        <div className="absolute bottom-[-7px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-black"></div>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div
                      onClick={() => handleRowClick(call.lead_id)}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 py-3 text-black dark:text-white text-[12px]"
                    >
                      {`${call.call_date} ${call.call_time}`}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <td
                  colSpan={5}
                  className="text-center py-4 text-black dark:text-white text-[12px]"
                >
                  No calls scheduled for today.
                </td>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
